import { Form, FormProps, Input, Modal, ModalProps } from 'antd';
import { IBank } from '../../interfaces/banks';
import React, { useCallback } from 'react';
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook';
import { confirmModalUnsavedChangesProps } from '../components/constants';
import { ButtonListFooterModalSM } from '../components/buttonsList';
import { clearFieldErrorMessageOnChange } from '../../utils/form.constants';
import {
  bankAddressRules,
  bankNameRules,
  bankSwiftRules,
  intermediaryAddressRules,
  intermediaryBankNameRules,
  intermediarySwiftRules,
} from './addBank.constants';
import SingleLineTextArea from '../../components/pages/payment-request/singleLineTextArea';

interface IProps {
  formProps: FormProps;
  modalProps: ModalProps;
  formIsLoading: boolean;
  formSubmit: (data: any) => void;
  close: () => void;
  changeBankData: (data: IBank) => void;
}

export const NewBankModalCreate = ({
  close,
  modalProps,
  formProps,
  formIsLoading,
  formSubmit,
  changeBankData,
}: IProps) => {
  const onSubmit = useCallback(
    (formValues: IBank) => {
      const { name, swift, legalAddress, intermediaryBank, intermediarySwift, intermediaryAddress } = formValues;
      changeBankData({
        name,
        swift,
        country: swift.substring(4, 6).toUpperCase(),
        legalAddress,
        intermediaryBank: intermediaryBank?.length ? intermediaryBank : undefined,
        intermediarySwift: intermediarySwift?.length ? intermediarySwift : undefined,
        intermediaryAddress: intermediaryAddress?.length ? intermediaryAddress : undefined,
      });
      formSubmit({});
    },
    [formSubmit, changeBankData],
  );

  const onSave = useCallback(() => {
    formProps.form?.submit();
  }, [formProps]);

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: close,
  });

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(formProps.form?.getFieldsValue()).some(value => value !== undefined);
    if (formIsNotEmpty) {
      openNotification();
    } else {
      close();
    }
  }, [close, formProps.form, openNotification]);

  const afterClose = useCallback(() => {
    formProps.form?.resetFields();
  }, [formProps]);

  return (
    <>
      <Modal
        {...modalProps}
        title='Add new bank'
        style={{ maxWidth: '400px', textAlign: 'center', marginTop: '100px' }}
        afterClose={afterClose}
        onCancel={onModalCancel}
        footer={ButtonListFooterModalSM(onSave, onModalCancel, formIsLoading)}
      >
        {contextHolder}
        <Form
          {...formProps}
          layout='vertical'
          onFinish={onSubmit}
          disabled={formIsLoading}
          onValuesChange={clearFieldErrorMessageOnChange(formProps.form)}
        >
          <Form.Item name='name' rules={bankNameRules}>
            <SingleLineTextArea
              placeholder='Bank name'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='swift' rules={bankSwiftRules}>
            <Input
              placeholder='SWIFT'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='legalAddress' rules={bankAddressRules}>
            <SingleLineTextArea
              placeholder='Bank address: City/Country'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='intermediaryBank' rules={intermediaryBankNameRules}>
            <SingleLineTextArea
              placeholder='Intermediary Bank name'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='intermediarySwift' rules={intermediarySwiftRules}>
            <Input
              placeholder='Intermediary SWIFT'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
          <Form.Item name='intermediaryAddress' rules={intermediaryAddressRules}>
            <SingleLineTextArea
              placeholder='Intermediary City/Country'
              style={{
                height: '35px',
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
